import assetLibraryClient from '@/api/assetLibraryClient';

/**
 * Briefs, as `GET /briefs` returns them (`BriefOut` in `api/briefs.py`).
 *
 * The one definition the basket and My Briefs both read: the basket creates
 * briefs, My Briefs lists them, and two copies of the shape would drift.
 */

export type BriefStatus = 'draft' | 'requested';

/** `pending` until a submit judges the line, then `ordered` or `skipped`. */
export type BriefLineStatus = 'pending' | 'ordered' | 'skipped';

export interface BriefLineOut {
  id: string;
  /** The media-plan row's id (Content Tracker's campaign UUID), not its code. */
  campaign_id: string;
  /** The row's human-readable code. Null on lines saved before it was captured. */
  campaign_code: string | null;
  asset_id: string;
  // A snapshot of the row, taken when the line was added: the row itself can
  // change or be archived after.
  market: string | null;
  size: string | null;
  platform: string | null;
  channel: string | null;
  format: string | null;
  quarter: string | null;
  nameplate: string | null;
  tier: string | null;
  status: BriefLineStatus;
  /** Why a line was skipped. Null otherwise. */
  result_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface BriefOut {
  id: string;
  /** The creator's email. */
  created_by: string;
  channel: string | null;
  /** The one market the brief covers; null on older briefs spanning several. */
  market: string | null;
  status: BriefStatus;
  /** Allocated on the first submit that orders anything; null before. */
  reference: string | null;
  needed_by: string | null;
  notes: string | null;
  requested_at: string | null;
  created_at: string;
  updated_at: string;
  lines: BriefLineOut[];
}

/** The backend's default page size; also how the end of the list is spotted. */
export const BRIEFS_PAGE_SIZE = 20;

export interface ListBriefsParams {
  status: BriefStatus;
  channel?: string;
  offset?: number;
}

/**
 * One page of the caller's own briefs, newest first. The backend scopes the
 * list to the caller, so there is no user parameter to get wrong.
 */
export async function listBriefs({ status, channel, offset = 0 }: ListBriefsParams) {
  const response = await assetLibraryClient.get<{ briefs: BriefOut[] }>('/briefs', {
    params: { status, channel: channel || undefined, limit: BRIEFS_PAGE_SIZE, offset },
  });
  return response.data.briefs;
}

/**
 * One brief, if the caller may see it. A brief they may not see is a 404, the
 * same as one that doesn't exist — the backend never confirms either way.
 */
export async function getBrief(briefId: string): Promise<BriefOut> {
  const response = await assetLibraryClient.get<BriefOut>(`/briefs/${briefId}`);
  return response.data;
}

/** What can change on a draft. Absent fields are left as they are. */
export interface BriefPatch {
  needed_by?: string | null;
  notes?: string | null;
  /**
   * The brief's `updated_at` as last seen. Given and stale, the edit is
   * refused (412) instead of written over a newer version.
   */
  expected_updated_at?: string;
  /**
   * The draft's whole line set, replacing the old one — every line comes back
   * pending, and its assets are checked again. Send only when lines changed.
   */
  lines?: { campaign_id: string; asset_id: string }[];
}

export async function updateBrief(briefId: string, patch: BriefPatch): Promise<BriefOut> {
  const response = await assetLibraryClient.patch<BriefOut>(`/briefs/${briefId}`, patch);
  return response.data;
}

export async function deleteBrief(briefId: string): Promise<void> {
  await assetLibraryClient.delete(`/briefs/${briefId}`);
}

export interface BriefSubmitResult {
  /** Requested if anything was ordered; still a draft, with reasons, if not. */
  brief: BriefOut;
  promoted_count: number;
  skipped_count: number;
}

/** Send a saved draft to production. Only its creator may. */
export async function submitBrief(briefId: string): Promise<BriefSubmitResult> {
  const response = await assetLibraryClient.post<BriefSubmitResult>(`/briefs/${briefId}/submit`);
  return response.data;
}

/**
 * Channels a brief can be for, from the shared taxonomy's channels
 * (`platform_rules.json`). A brief takes its channel from its rows' platforms.
 */
export const BRIEF_CHANNELS = [
  { value: 'social', label: 'Social' },
  { value: 'search', label: 'Search' },
] as const;

export function channelLabel(channel: string | null): string {
  if (!channel) return '—';
  return BRIEF_CHANNELS.find((option) => option.value === channel)?.label ?? channel;
}

export interface BriefSummary {
  /** Distinct media-plan rows the brief covers. */
  rowCount: number;
  /** Assets across all rows. */
  lineCount: number;
  /** Distinct markets, sorted. */
  markets: string[];
  orderedRowCount: number;
  skippedRowCount: number;
}

/**
 * What the backend writes on a row's *other* lines when one of them fails
 * (`REASON_ROW_REJECTED` in `brief_service.py`): the row is skipped together,
 * but this says nothing about what to fix.
 */
export const ROW_REJECTED_REASON = 'Rejected alongside another line for this campaign';

export interface RowOutcome {
  status: BriefLineStatus;
  /**
   * Why the row was skipped: the reason of the line that actually failed, not
   * the "rejected alongside" written on its neighbours. Null unless skipped.
   */
  reason: string | null;
}

/**
 * Each media-plan row's outcome, from its lines: skipped if any line was,
 * ordered only if every line was, pending otherwise. The one rule the basket's
 * confirmation and My Briefs both read, so the two never disagree about a row.
 *
 * The backend orders or skips a row's lines together, so a mixed row should
 * not occur; this decides it anyway, and without depending on line order.
 */
export function rowOutcomes(
  lines: readonly Pick<BriefLineOut, 'campaign_id' | 'status' | 'result_reason'>[],
): Map<string, RowOutcome> {
  const linesByRow = new Map<string, Pick<BriefLineOut, 'status' | 'result_reason'>[]>();
  for (const line of lines) {
    linesByRow.set(line.campaign_id, [...(linesByRow.get(line.campaign_id) ?? []), line]);
  }
  const outcomes = new Map<string, RowOutcome>();
  for (const [rowId, rowLines] of linesByRow) {
    const skippedLines = rowLines.filter((line) => line.status === 'skipped');
    const cause =
      skippedLines.find(
        (line) => line.result_reason && line.result_reason !== ROW_REJECTED_REASON,
      ) ?? skippedLines[0];
    outcomes.set(
      rowId,
      cause
        ? { status: 'skipped', reason: cause.result_reason }
        : rowLines.every((line) => line.status === 'ordered')
          ? { status: 'ordered', reason: null }
          : { status: 'pending', reason: null },
    );
  }
  return outcomes;
}

/**
 * What a list row says about a brief. Counted per media-plan row rather than
 * per line: "3 of 5 rows" is the unit the user briefed in.
 */
export function summarizeBrief(brief: BriefOut): BriefSummary {
  const markets = new Set<string>();
  for (const line of brief.lines) {
    if (line.market) markets.add(line.market);
  }
  const statuses = Array.from(rowOutcomes(brief.lines).values(), (outcome) => outcome.status);
  return {
    rowCount: statuses.length,
    lineCount: brief.lines.length,
    markets: Array.from(markets).sort(),
    orderedRowCount: statuses.filter((status) => status === 'ordered').length,
    skippedRowCount: statuses.filter((status) => status === 'skipped').length,
  };
}
